import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminWordSearch() {
  const [puzzles, setPuzzles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPuzzle, setEditingPuzzle] = useState(null);
  const [formData, setFormData] = useState({
    tema: '',
    dificuldade: 'FACIL',
    palavras: [''],
    grid_size: 10,
    plan_required: 'FREE',
    pontos_maximos: 50
  });

  useEffect(() => {
    loadPuzzles();
  }, []);

  const loadPuzzles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/wordsearch/puzzles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPuzzles(response.data);
    } catch (error) {
      toast.error('Erro ao carregar puzzles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const cleanData = {
        ...formData,
        palavras: formData.palavras.filter(p => p.trim() !== '')
      };
      
      if (editingPuzzle) {
        await axios.put(`${BACKEND_URL}/api/admin/wordsearch/puzzles/${editingPuzzle.id}`, cleanData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Puzzle atualizado');
      } else {
        await axios.post(`${BACKEND_URL}/api/wordsearch/puzzles`, cleanData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Puzzle criado');
      }
      
      setShowDialog(false);
      setEditingPuzzle(null);
      resetForm();
      loadPuzzles();
    } catch (error) {
      toast.error('Erro ao salvar puzzle');
    }
  };

  const deletePuzzle = async (puzzleId) => {
    if (!window.confirm('Tem certeza que deseja excluir este puzzle?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/wordsearch/puzzles/${puzzleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Puzzle excluído');
      loadPuzzles();
    } catch (error) {
      toast.error('Erro ao excluir puzzle');
    }
  };

  const openEditDialog = (puzzle) => {
    setEditingPuzzle(puzzle);
    setFormData({
      tema: puzzle.tema,
      dificuldade: puzzle.dificuldade,
      palavras: puzzle.palavras,
      grid_size: puzzle.grid_size,
      plan_required: puzzle.plan_required,
      pontos_maximos: puzzle.pontos_maximos
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      tema: '',
      dificuldade: 'FACIL',
      palavras: [''],
      grid_size: 10,
      plan_required: 'FREE',
      pontos_maximos: 50
    });
  };

  const addPalavraField = () => {
    setFormData({ ...formData, palavras: [...formData.palavras, ''] });
  };

  const removePalavraField = (index) => {
    const newPalavras = formData.palavras.filter((_, i) => i !== index);
    setFormData({ ...formData, palavras: newPalavras });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Gerenciar Caça-Palavras
              </h1>
              <p className="text-muted-foreground text-lg">
                Total: {puzzles.length} puzzles
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingPuzzle(null);
                resetForm();
                setShowDialog(true);
              }}
              className="bg-gradient-to-r from-orange-500 to-red-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Puzzle
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {puzzles.map((puzzle) => (
              <div key={puzzle.id} className="p-6 rounded-2xl bg-white border border-border">
                <h3 className="text-xl font-bold mb-3">{puzzle.tema}</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Dificuldade:</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      puzzle.dificuldade === 'FACIL' ? 'bg-green-100 text-green-700' :
                      puzzle.dificuldade === 'MEDIO' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {puzzle.dificuldade}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Palavras:</span>
                    <span className="font-semibold">{puzzle.palavras.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Grade:</span>
                    <span className="font-semibold">{puzzle.grid_size}x{puzzle.grid_size}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Pontos Máx:</span>
                    <span className="font-semibold">{puzzle.pontos_maximos}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Plano:</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      puzzle.plan_required === 'PREMIUM' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {puzzle.plan_required}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => openEditDialog(puzzle)}
                    size="sm"
                    variant="outline"
                    className="flex-1"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                  <Button
                    onClick={() => deletePuzzle(puzzle.id)}
                    size="sm"
                    variant="destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPuzzle ? 'Editar Puzzle' : 'Novo Puzzle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Tema</label>
              <Input
                value={formData.tema}
                onChange={(e) => setFormData({ ...formData, tema: e.target.value })}
                placeholder="Ex: Países da América do Sul"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Palavras</label>
              {formData.palavras && formData.palavras[0] !== undefined && (
                <div className="flex gap-2 mb-2">
                  <Input
                    value={formData.palavras[0]}
                    onChange={(e) => {
                      const newPalavras = [...formData.palavras];
                      newPalavras[0] = e.target.value.toUpperCase();
                      setFormData({ ...formData, palavras: newPalavras });
                    }}
                    placeholder="PALAVRA 1"
                    className="flex-1"
                  />
                </div>
              )}
              {formData.palavras && formData.palavras[1] !== undefined && (
                <div className="flex gap-2 mb-2">
                  <Input
                    value={formData.palavras[1]}
                    onChange={(e) => {
                      const newPalavras = [...formData.palavras];
                      newPalavras[1] = e.target.value.toUpperCase();
                      setFormData({ ...formData, palavras: newPalavras });
                    }}
                    placeholder="PALAVRA 2"
                    className="flex-1"
                  />
                </div>
              )}
              {formData.palavras && formData.palavras[2] !== undefined && (
                <div className="flex gap-2 mb-2">
                  <Input
                    value={formData.palavras[2]}
                    onChange={(e) => {
                      const newPalavras = [...formData.palavras];
                      newPalavras[2] = e.target.value.toUpperCase();
                      setFormData({ ...formData, palavras: newPalavras });
                    }}
                    placeholder="PALAVRA 3"
                    className="flex-1"
                  />
                </div>
              )}
              {formData.palavras && formData.palavras[3] !== undefined && (
                <div className="flex gap-2 mb-2">
                  <Input
                    value={formData.palavras[3]}
                    onChange={(e) => {
                      const newPalavras = [...formData.palavras];
                      newPalavras[3] = e.target.value.toUpperCase();
                      setFormData({ ...formData, palavras: newPalavras });
                    }}
                    placeholder="PALAVRA 4"
                    className="flex-1"
                  />
                </div>
              )}
              {formData.palavras && formData.palavras[4] !== undefined && (
                <div className="flex gap-2 mb-2">
                  <Input
                    value={formData.palavras[4]}
                    onChange={(e) => {
                      const newPalavras = [...formData.palavras];
                      newPalavras[4] = e.target.value.toUpperCase();
                      setFormData({ ...formData, palavras: newPalavras });
                    }}
                    placeholder="PALAVRA 5"
                    className="flex-1"
                  />
                </div>
              )}
              <Button
                type="button"
                onClick={addPalavraField}
                size="sm"
                variant="outline"
                className="w-full mt-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Palavra
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Dificuldade</label>
                <select
                  value={formData.dificuldade}
                  onChange={(e) => setFormData({ ...formData, dificuldade: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-white"
                >
                  <option value="FACIL">Fácil</option>
                  <option value="MEDIO">Médio</option>
                  <option value="DIFICIL">Difícil</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Tamanho da Grade</label>
                <Input
                  type="number"
                  value={formData.grid_size}
                  onChange={(e) => setFormData({ ...formData, grid_size: parseInt(e.target.value) })}
                  min="8"
                  max="20"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Pontos Máximos</label>
                <Input
                  type="number"
                  value={formData.pontos_maximos}
                  onChange={(e) => setFormData({ ...formData, pontos_maximos: parseInt(e.target.value) })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Plano</label>
                <select
                  value={formData.plan_required}
                  onChange={(e) => setFormData({ ...formData, plan_required: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-white"
                >
                  <option value="FREE">Grátis</option>
                  <option value="PREMIUM">Premium</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-orange-500 to-red-500">
                {editingPuzzle ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
